import React from "react";
import * as ReactDOM from "react-dom";
import { Icon, emitAutomation, computeQuoteView, fenceSpecFrom, quoteBom, confirmDialog, notify } from "./registry";
import { VerticalChip } from "./registry";   // "Concrete" on a job from the other service; nothing on a fence job
import { throttledGeocode } from "./registry";   // the one shared geocode queue (workorders.tsx) — the board pins a job once and keeps it as wo.geo
import { jobWeather, nextGoodDay, geoOf, weatherFlagLive, crewBusyOn, dayLabel as wxDayLabel, addYmd as wxAddYmd } from "./lib/schedulewx";   // weather-gated scheduling (plan §13 item 13)
import { verticalOf, jobTypesOf } from "./lib/verticals";   // a concrete day is graded by its job types (lib/weatherrules.ts)
import { outboxConfigured } from "./lib/fieldsync";   // FW-007: "On my way" as a queued crew op
import { totalMinutes, ratesWithDefaults } from "./lib/steptime";
import { buildStepsFor, bomStepCounts } from "./lib/buildsteps";
import { localYmd } from "./lib/dates";
import { coHold, confirmCoHold } from "./lib/changeorders";
/* ============================================================
   Fence Works — Schedule (interactive dispatch board)
   Jobber-style board: days are columns, each crew is a lane.
   Drag a job to another day → reschedule; to another lane →
   reassign crew. Drag from the unscheduled tray to put a job
   on the board. Auto-schedule stays as a one-click fallback.
   Month view keeps the classic color-coded agenda.

   "Day · hours" adds an hourly time-grid (Jobber's hourly
   schedule): time rows × crew columns, each job a time-block at
   its start time, sized by its duration. Appointment times live
   on two BACKWARD-COMPATIBLE work-order fields, stored through the
   flexible shape (no types.ts edit needed):
     • scheduledTime  "HH:MM" (24h) — the appointment start time
     • durationMin    number        — block length in minutes
   A job with no scheduledTime is "unslotted": it stays on its day
   but rides an all-day strip at the top of the grid until dropped
   onto a time slot. Board / Week / Month never read these fields,
   so jobs without a time keep working everywhere exactly as before.
   ============================================================ */
const CAL_money0 = window.VFP.money0;

const ymd = (d) => localYmd(d);   // LOCAL date parts — UTC rolls over at 7-8 PM in the US
const parseYmd = (s) => s ? new Date(s + "T00:00:00") : null;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const DEFAULT_WORKDAYS = [1, 2, 3, 4, 5];
function workdaysOf(store) {
  const w = store && store.settings && store.settings.workdays;
  return (Array.isArray(w) && w.length) ? w : DEFAULT_WORKDAYS;
}
const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;
function nextWeekday(d, days) {
  const allowed = (days && days.length) ? days : DEFAULT_WORKDAYS;
  let x = new Date(d), guard = 0;
  while (!allowed.includes(x.getDay()) && guard++ < 14) x = addDays(x, 1);
  return x;
}
function woDays(wo, crew) {
  if (!crew || !wo.crewHours) return 1;
  const cap = window.VFP.crewHeadcount(crew) * (Number(crew.hoursPerDay) || 8);
  return cap > 0 ? Math.max(1, Math.ceil((Number(wo.crewHours) || 0) / cap)) : 1;
}
/* whole-day span of a job as a count (>=1), from its start/end ymd strings */
function spanDays(wo) {
  const s = parseYmd(wo.scheduledStart), e = parseYmd(wo.scheduledEnd || wo.scheduledStart);
  if (!s || !e) return 1;
  return Math.max(1, Math.round((e.getTime() - s.getTime()) / 86400000) + 1);
}

/* ---------- hourly time-grid model (Day · hours) ---------- */
const GRID_START_HOUR = 7;     // first row shown (7 AM)
const GRID_END_HOUR = 19;      // last row shown ends here (7 PM)
const SLOT_MIN = 30;           // grid granularity — drop snaps to the half-hour
const PX_PER_MIN = 56 / 60;    // row height: 56px per hour
const DEFAULT_DURATION_MIN = 240;   // fallback block length when no estimate (~4h)
const MIN_DURATION_MIN = 30;        // never size a block below one slot

/* "HH:MM" 24h → minutes-from-midnight (null when malformed) */
function hhmmToMin(s) {
  if (typeof s !== "string") return null;
  const m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = +m[1], mm = +m[2];
  if (h < 0 || h > 23 || mm < 0 || mm > 59) return null;
  return h * 60 + mm;
}
/* minutes-from-midnight → "HH:MM" 24h (for storage / <input type=time>) */
function minToHHMM(min) {
  const t = Math.max(0, Math.min(24 * 60 - 1, Math.round(min)));
  const h = Math.floor(t / 60), mm = t % 60;
  return String(h).padStart(2, "0") + ":" + String(mm).padStart(2, "0");
}
/* minutes-from-midnight → "7:30 AM" (human display) */
function fmtClock(min) {
  if (min == null || isNaN(min)) return "";
  let h = Math.floor(min / 60), mm = Math.round(min % 60);
  if (mm === 60) { mm = 0; h += 1; }
  const ap = h >= 12 ? "PM" : "AM";
  let h12 = h % 12; if (h12 === 0) h12 = 12;
  return h12 + (mm ? ":" + String(mm).padStart(2, "0") : "") + " " + ap;
}
/* a slotted job's duration in minutes (stored, else its estimate, else default) */
function jobDurationMin(wo) {
  const d = Number(wo && wo.durationMin);
  return isFinite(d) && d > 0 ? Math.max(MIN_DURATION_MIN, d) : DEFAULT_DURATION_MIN;
}

/* Default block length for a job, in minutes — derived from the same estimates
   the crew sheet and work-order labor card already use, so a dropped job sizes
   itself sensibly without the dispatcher typing a duration:
     1. build-step time estimate (lib/steptime totalMinutes over the linked
        quote's steps) — the per-step "~Xh Ym" the crew sheet shows;
     2. else the quote's labor crew-hours (computeQuoteView) ÷ headcount, so 16
        crew-hrs with a 2-person crew ≈ an 8h on-site day;
     3. else the work order's own crewHours estimate ÷ headcount;
     4. else ~4h.
   Capped to a single on-site day so a multi-day job still drops as one block. */
function estimateDurationMin(wo, store, crews) {
  if (!wo) return DEFAULT_DURATION_MIN;
  const crew = (crews || []).find(c => c.id === wo.crewId);
  // crew-hours / step person-minutes are TOTAL work; ÷ headcount → wall-clock
  // on-site time (16 crew-hrs with a 2-person crew ≈ an 8h day).
  const heads = crew ? Math.max(1, window.VFP.crewHeadcount(crew)) : 1;
  const q = wo.quoteId ? (store.quotes || []).find(x => x.id === wo.quoteId) : null;
  if (q) {
    try {
      const view = computeQuoteView(q, store.settings || {});
      // a concrete quote: the recipe's crew-hours (lib/engine.concrete.ts), never
      // the fence build steps — its "product" has no fence kind to size
      const cx = view && view.quote && view.quote.concrete && view.quote.concrete.time;
      if (cx && Number(cx.crewHours) > 0) return clampDayMinutes((Number(cx.crewHours) / heads) * 60);
      // 1) build-step time estimate (mirrors crewsheet's sectionSteps → totalMinutes);
      //    per-section rates layer the product's sparse overrides over the global table
      const globalRates = store.settings && store.settings.stepRates;
      const stepMin = stepMinutesForView(view, globalRates);
      if (stepMin > 0) return clampDayMinutes(stepMin / heads);   // shared across the crew
      // 2) labor crew-hours ÷ headcount → on-site hours
      const lh = view && view.quote && Number(view.quote.laborHours);
      if (isFinite(lh) && lh > 0) return clampDayMinutes((lh / heads) * 60);
    } catch (e) { /* fall through to the work-order estimate */ }
  }
  // 3) work order's own crew-hours estimate
  const wh = Number(wo.crewHours);
  if (isFinite(wh) && wh > 0) return clampDayMinutes((wh / heads) * 60);
  return DEFAULT_DURATION_MIN;
}
/* sum build-step minutes across every fence section of a computed quote view —
   the engine's per-step estimate, the same totalMinutes(steps, counts) the crew
   build sheet shows. Each section resolves its product's sparse step-rate
   overrides over `globalRates` and threads its footage (calc.LF) so the flat
   steps' optional size scaling applies, mirroring the crew sheet. (We don't
   thread the parcel survey runs through buildStepsFor here, so graded jobs
   estimate a touch lower than the sheet — only the flat survey steps differ;
   close enough for a default block length.) */
function stepMinutesForView(view, globalRates) {
  if (!view) return 0;
  const secs = view.fenceQuotes
    ? view.fenceQuotes.map(f => ({ prod: f.prod, calc: f.q }))
    : [{ prod: view.product, calc: view.quote }];
  let total = 0;
  for (const s of secs) {
    try {
      if (!s.prod || !s.calc) continue;
      const spec = fenceSpecFrom(s.prod);
      const counts = bomStepCounts(quoteBom(s.calc, s.prod), { posts: s.calc.posts, gates: s.calc.gates });
      const steps = buildStepsFor({ ...spec, gates: s.calc.gates | 0 });
      const secRates = ratesWithDefaults(globalRates, s.prod.stepRates);
      const feet = +s.calc.LF || 0;
      total += totalMinutes(steps, counts, secRates, feet);
    } catch (e) { /* skip a section we can't price */ }
  }
  return total;
}
/* keep an estimated block within one on-site day (1 slot … the grid's window) */
function clampDayMinutes(min) {
  const cap = (GRID_END_HOUR - GRID_START_HOUR) * 60;
  return Math.max(MIN_DURATION_MIN, Math.min(cap, Math.round(min / 15) * 15));   // snap to 15m
}

/* phones can't show 7 lanes×days side-by-side without horizontal overflow, so the
   board degrades to a single-day column on narrow screens (still fully usable). */
function useNarrow(maxWidth = 640) {
  const get = () => { try { return window.matchMedia(`(max-width: ${maxWidth}px)`).matches; } catch (e) { return false; } };
  const [narrow, setNarrow] = React.useState<any>(get);
  React.useEffect(() => {
    let mq: any; try { mq = window.matchMedia(`(max-width: ${maxWidth}px)`); } catch (e) { return; }
    const on = () => setNarrow(mq.matches);
    on();
    if (mq.addEventListener) mq.addEventListener("change", on); else if (mq.addListener) mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener("change", on); else if (mq.removeListener) mq.removeListener(on); };
  }, [maxWidth]);
  return narrow;
}

/* Fire the upcoming-appointment automation when a job lands on the calendar.
   Enriches the customer with phone/email from the customer record (mirrors
   updateWorkOrder's custOf) so the reminder/confirmation has somewhere to send.
   Backend owns the send-timing — we just hand it the appointment date. No-op
   when the automation service isn't configured (emitAutomation guards). */
function emitAppointment(store, wo, scheduledStart) {
  const c: any = (store.customers || []).find((x: any) => x.id === wo.customerId) || {};
  emitAutomation("appointment.upcoming", {
    customer: { id: wo.customerId, name: wo.customer || c.name, phone: c.phone, email: c.email },
    job: { id: wo.projectId || wo.id, date: scheduledStart, address: wo.address || c.address },
  });
}

/* ============================================================
   Calendar sync (Google / Apple / Outlook) + manual events
   ------------------------------------------------------------
   settings.calendarSync = { provider: "google"|"apple"|"outlook"|"", twoWay }
   settings.calendarEvents = [{ id, title, date, time?, durationMin?, location?, notes? }]

   Google is the real, connected sync (OAuth via Integrations → the server
   pushes/updates/removes events; see server/src/integrations/gcal.js). With
   2-way sync on, every schedule / reschedule / unschedule on this board syncs
   the Google event automatically — no per-work-order button needed.
   Apple & Outlook are one-way today: open-in-app links, per-event quick-add,
   and a whole-schedule .ics export that imports into either.
   ============================================================ */
function calSyncOf(store) {
  const cs = (store.settings && store.settings.calendarSync) || {};
  return { provider: cs.provider || "", twoWay: cs.twoWay !== false };
}
function gcalConn(store) {
  const ig = store.settings && store.settings.integrations;
  return (ig && ig.gcal && ig.gcal.connected) ? ig.gcal : null;
}
/* Auto-push a just-scheduled/moved job to Google Calendar (2-way sync on).
   Fire-and-forget: a failed push never blocks the board — the per-work-order
   button remains as the manual fallback. */
function gcalAutoPush(store, wo, patch) {
  try {
    const { provider, twoWay } = calSyncOf(store);
    if (!twoWay || (provider && provider !== "google")) return;
    const conn = gcalConn(store);
    const DB: any = window.VFP_DB;
    if (!(conn && DB && DB.gcalSync && DB.apiBase && DB.apiBase())) return;
    const merged = { ...wo, ...patch };
    const crews = (store.settings && store.settings.crews) || [];
    const crew = crews.find(c => c.id === merged.crewId);
    DB.gcalSync({ ...merged, crew: crew && crew.name }, conn.calendarId)
      .then((r: any) => {
        if (r && r.eventId) store.updateWorkOrder(wo.id, { gcalEventId: r.eventId, calendarAdded: true, calendarStale: false });
        else notify("Couldn't reach Google Calendar — update it manually", { kind: "error" });
      })
      .catch(() => notify("Couldn't reach Google Calendar — update it manually", { kind: "error" }));
  } catch (e) { /* sync is best-effort */ }
}
/* Remove the Google event when a job comes off the calendar (2-way sync on). */
function gcalAutoRemove(store, wo) {
  try {
    const { provider, twoWay } = calSyncOf(store);
    if (!twoWay || (provider && provider !== "google")) return;
    const DB: any = window.VFP_DB;
    // a failed delete leaves a stale event on Google — keep the id + flag it so it can be cleaned up
    if (wo.gcalEventId && DB && DB.gcalUnsync) DB.gcalUnsync(wo.gcalEventId).catch(() => {
      store.updateWorkOrder(wo.id, { gcalEventId: wo.gcalEventId, calendarStale: true });
      notify("This job is still on Google Calendar — remove it manually", { kind: "error" });
    });
  } catch (e) {}
}

/* ---- manual events: notes on the calendar that aren't work orders ---- */
function calEventsOf(store) {
  const ev = store.settings && store.settings.calendarEvents;
  return Array.isArray(ev) ? ev : [];
}
function saveCalEvent(store, ev) {
  const cur = calEventsOf(store);
  const next = cur.some(x => x.id === ev.id) ? cur.map(x => x.id === ev.id ? ev : x) : [...cur, ev];
  store.setSettings({ calendarEvents: next });
}
function deleteCalEvent(store, id) {
  store.setSettings({ calendarEvents: calEventsOf(store).filter(x => x.id !== id) });
}

/* ---- add-to-calendar links & .ics for a manual event ---- */
const icsEsc = (v) => String(v || "").replace(/\\/g, "\\\\").replace(/[,;]/g, m => "\\" + m).replace(/\r?\n/g, "\\n");
const ymdCompact = (s) => String(s || "").replace(/-/g, "");
function evTimes(ev) {
  const startMin = hhmmToMin(ev.time);
  const allDay = startMin == null;
  const dur = Math.max(15, Number(ev.durationMin) || 60);
  const pad = (n) => String(n).padStart(2, "0");
  const stamp = (min) => { const h = Math.floor(min / 60) % 24, m = min % 60; return "T" + pad(h) + pad(m) + "00"; };
  return {
    allDay,
    // floating local times — right for "meet the inspector at 9" wherever the shop is
    dtStart: allDay ? ymdCompact(ev.date) : ymdCompact(ev.date) + stamp(startMin),
    dtEnd: allDay ? ymdCompact(ymd(addDays(parseYmd(ev.date), 1))) : ymdCompact(ev.date) + stamp(startMin + dur),
    isoStart: allDay ? ev.date : ev.date + "T" + minToHHMM(startMin) + ":00",
    isoEnd: allDay ? ymd(addDays(parseYmd(ev.date), 1)) : ev.date + "T" + minToHHMM(startMin + dur) + ":00",
  };
}
function evGoogleUrl(ev) {
  const t = evTimes(ev);
  return "https://calendar.google.com/calendar/render?action=TEMPLATE&text=" + encodeURIComponent(ev.title || "Event")
    + "&dates=" + t.dtStart + "/" + t.dtEnd
    + "&details=" + encodeURIComponent(ev.notes || "") + "&location=" + encodeURIComponent(ev.location || "");
}
function evOutlookUrl(ev) {
  const t = evTimes(ev);
  return "https://outlook.live.com/calendar/0/deeplink/compose?path=%2Fcalendar%2Faction%2Fcompose&rru=addevent"
    + "&subject=" + encodeURIComponent(ev.title || "Event")
    + "&startdt=" + encodeURIComponent(t.isoStart) + "&enddt=" + encodeURIComponent(t.isoEnd)
    + (t.allDay ? "&allday=true" : "")
    + "&body=" + encodeURIComponent(ev.notes || "") + "&location=" + encodeURIComponent(ev.location || "");
}
function downloadIcsFile(name, lines) {
  const ics = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Fence Works//Schedule//EN", ...lines, "END:VCALENDAR"].join("\r\n");
  const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar" }));
  const a = document.createElement("a"); a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
function evVevent(ev) {
  const t = evTimes(ev);
  return [
    "BEGIN:VEVENT",
    "UID:" + ev.id + "@fenceworks.ai",
    "DTSTAMP:" + new Date().toISOString().replace(/[-:]/g, "").slice(0, 15) + "Z",
    t.allDay ? "DTSTART;VALUE=DATE:" + t.dtStart : "DTSTART:" + t.dtStart,
    t.allDay ? "DTEND;VALUE=DATE:" + t.dtEnd : "DTEND:" + t.dtEnd,
    "SUMMARY:" + icsEsc(ev.title || "Event"),
    ev.location ? "LOCATION:" + icsEsc(ev.location) : null,
    ev.notes ? "DESCRIPTION:" + icsEsc(ev.notes) : null,
    "END:VEVENT",
  ].filter(Boolean);
}
/* Whole-schedule export: every scheduled job + every manual event, one .ics.
   Imports into Apple Calendar and Outlook (and Google, for the OAuth-averse). */
function exportScheduleIcs(store) {
  const crews = (store.settings && store.settings.crews) || [];
  const lines: any[] = [];
  (store.workOrders || []).filter(o => (o.status === "scheduled" || o.status === "in_progress") && o.scheduledStart).forEach(o => {
    const crew = crews.find(c => c.id === o.crewId);
    const startMin = hhmmToMin(o.scheduledTime);
    const timed = startMin != null;
    const dur = timed ? jobDurationMin(o) : 0;
    const pad = (n) => String(n).padStart(2, "0");
    const stamp = (min) => "T" + pad(Math.floor(min / 60) % 24) + pad(min % 60) + "00";
    lines.push(
      "BEGIN:VEVENT",
      "UID:" + o.id + "@fenceworks.ai",
      "DTSTAMP:" + new Date().toISOString().replace(/[-:]/g, "").slice(0, 15) + "Z",
      timed ? "DTSTART:" + ymdCompact(o.scheduledStart) + stamp(startMin)
            : "DTSTART;VALUE=DATE:" + ymdCompact(o.scheduledStart),
      timed ? "DTEND:" + ymdCompact(o.scheduledStart) + stamp(startMin + dur)
            : "DTEND;VALUE=DATE:" + ymdCompact(ymd(addDays(parseYmd(o.scheduledEnd || o.scheduledStart), 1))),
      "SUMMARY:" + icsEsc((o.customer ? o.customer + " — " : "") + (o.title || "Fence install")),
      o.address ? "LOCATION:" + icsEsc(o.address) : null,
      "DESCRIPTION:" + icsEsc([crew ? "Crew: " + crew.name : null, o.total ? "Job total: " + CAL_money0(o.total) : null, o.notes || null, "— from Fence Works"].filter(Boolean).join("\n")),
      "END:VEVENT",
    );
  });
  calEventsOf(store).forEach(ev => lines.push(...evVevent(ev)));
  downloadIcsFile("fenceworks-schedule.ics", lines.filter(Boolean));
}

/* ---- auto-schedule: pack unscheduled jobs onto the next open crew workdays ---- */
function autoSchedule(store) {
  const crews = (store.settings && store.settings.crews) || [];
  const orders = store.workOrders || [];
  const unscheduled = orders.filter(o => o.status === "unscheduled");
  if (!unscheduled.length) return 0;
  const days = workdaysOf(store);

  // never schedule same-day: start from the next allowed workday AFTER today
  const nextFree: any = {};
  const start0 = nextWeekday(addDays(new Date(), 1), days);
  crews.forEach(c => { nextFree[c.id] = new Date(start0); });
  nextFree.__none = new Date(start0);
  orders.filter(o => o.status === "scheduled" && o.scheduledEnd).forEach(o => {
    const key = o.crewId && nextFree[o.crewId] ? o.crewId : "__none";
    const end = parseYmd(o.scheduledEnd); if (!end) return;
    const after = nextWeekday(addDays(end, 1), days);
    if (after > nextFree[key]) nextFree[key] = after;
  });

  unscheduled.forEach(wo => {
    let crewId = wo.crewId;
    if (!crewId || !crews.find(c => c.id === crewId)) {
      if (crews.length) crewId = crews.slice().sort((a, b) => nextFree[a.id] - nextFree[b.id])[0].id;
    }
    const crew = crews.find(c => c.id === crewId);
    const crewDays = (crew && crew.workdays && crew.workdays.length) ? crew.workdays : days;   // per-crew workdays override
    const key = crewId && nextFree[crewId] ? crewId : "__none";
    // never schedule before materials arrive (ETA + 1 day)
    let earliest = new Date(nextFree[key]);
    if (wo.materialEta) { const matFloor = nextWeekday(addDays(parseYmd(wo.materialEta), 1), crewDays); if (matFloor > earliest) earliest = matFloor; }
    const start = nextWeekday(earliest, crewDays);
    const jobDays = woDays(wo, crew);
    let end = new Date(start), placed = 1;
    while (placed < jobDays) { end = nextWeekday(addDays(end, 1), crewDays); placed++; }
    store.updateWorkOrder(wo.id, { status: "scheduled", crewId, scheduledStart: ymd(start), scheduledEnd: ymd(end), calendarAdded: false });
    emitAppointment(store, wo, ymd(start));   // upcoming-appointment reminder/confirmation
    nextFree[key] = nextWeekday(addDays(end, 1), crewDays);
  });
  store.addActivity(`Auto-scheduled ${unscheduled.length} job${unscheduled.length > 1 ? "s" : ""}`);
  return unscheduled.length;
}

/* ---- shared scheduling mutations used by drag/drop + the crew day-sheet ---- */
// Move a job to a target day (keeps its multi-day span) and optionally a crew lane.
function moveJob(store, wo, dayStr, crewId, opts?: any) {
  if (!wo || !dayStr) return;
  const span = spanDays(wo);
  const start = parseYmd(dayStr);
  const end = addDays(start, span - 1);
  const patch: any = { scheduledStart: dayStr, scheduledEnd: ymd(end) };
  if (crewId !== undefined) patch.crewId = crewId || "";
  if (opts && opts.reason) patch.moveReason = String(opts.reason);   // "weather": the timeline says why (app.tsx lifts it into history)
  // a moved job no longer matches its previously-pushed Google Calendar event
  if (wo.calendarAdded) patch.calendarAdded = false;
  store.updateWorkOrder(wo.id, patch);
  gcalAutoPush(store, wo, patch);   // 2-way sync: reschedule updates the Google event
}
// Schedule a currently-unscheduled job by dropping it on a crew × day cell.
function scheduleOnto(store, wo, dayStr, crewId) {
  if (!wo || !dayStr) return;
  const crews = (store.settings && store.settings.crews) || [];
  const targetCrew = crewId !== undefined ? crewId : wo.crewId;
  // size the day-span by the target crew's capacity; if dropped on Unassigned,
  // fall back to the job's existing crew so a multi-day estimate isn't lost
  const crew = crews.find(c => c.id === targetCrew) || crews.find(c => c.id === wo.crewId);
  const jobDays = woDays(wo, crew);
  const start = parseYmd(dayStr);
  const end = addDays(start, jobDays - 1);
  const patch = {
    status: "scheduled", crewId: crewId !== undefined ? (crewId || "") : (wo.crewId || ""),
    scheduledStart: dayStr, scheduledEnd: ymd(end), calendarAdded: false,
  };
  store.updateWorkOrder(wo.id, patch);
  gcalAutoPush(store, wo, patch);       // 2-way sync: new schedule → Google event
  emitAppointment(store, wo, dayStr);   // upcoming-appointment reminder/confirmation
  store.addActivity((wo.customer || wo.title || "Job") + " scheduled for " + start.toLocaleDateString("en-US", { month: "short", day: "numeric" }));
}

/* ---- time-grid mutations (Day · hours): set an appointment TIME on a job ----
   These layer the backward-compatible scheduledTime/durationMin onto a job; the
   day-level scheduledStart/scheduledEnd keep their existing meaning so the board
   / week / month views are unaffected. */
// Give an already-scheduled job a time slot (and optionally re-lane it), keeping
// its day. Used when dragging a board/grid block onto a time slot. Preserves a
// multi-day span (re-anchors start to the dropped day, keeps the same length) so
// giving a multi-day job a time never silently shortens it to one day.
function slotJob(store, wo, dayStr, crewId, hhmm) {
  if (!wo || !dayStr) return;
  const span = spanDays(wo);
  const patch: any = { scheduledStart: dayStr, scheduledEnd: ymd(addDays(parseYmd(dayStr), span - 1)) };
  if (hhmm) patch.scheduledTime = hhmm;
  if (crewId !== undefined) patch.crewId = crewId || "";
  // default the block length from the estimate the first time a job is slotted
  if (!(Number(wo.durationMin) > 0)) {
    const crews = (store.settings && store.settings.crews) || [];
    const probe = crewId !== undefined ? { ...wo, crewId: crewId || "" } : wo;
    patch.durationMin = estimateDurationMin(probe, store, crews);
  }
  if (wo.calendarAdded) patch.calendarAdded = false;
  store.updateWorkOrder(wo.id, patch);
  gcalAutoPush(store, wo, patch);   // 2-way sync: re-timed job updates the Google event
}
// Schedule a currently-unscheduled job by dropping it on a time slot: that day,
// that crew column, at that time — single-day (time-grid is a one-day view).
function scheduleOntoSlot(store, wo, dayStr, crewId, hhmm) {
  if (!wo || !dayStr) return;
  const crews = (store.settings && store.settings.crews) || [];
  const targetCrewId = crewId !== undefined ? (crewId || "") : (wo.crewId || "");
  const dur = (Number(wo.durationMin) > 0)
    ? Number(wo.durationMin)
    : estimateDurationMin({ ...wo, crewId: targetCrewId }, store, crews);
  const patch = {
    status: "scheduled", crewId: targetCrewId,
    scheduledStart: dayStr, scheduledEnd: dayStr,
    scheduledTime: hhmm || minToHHMM(GRID_START_HOUR * 60), durationMin: dur,
    calendarAdded: false,
  };
  store.updateWorkOrder(wo.id, patch);
  gcalAutoPush(store, wo, patch);       // 2-way sync: new schedule → Google event
  emitAppointment(store, wo, dayStr);   // upcoming-appointment reminder/confirmation
  const at = hhmmToMin(hhmm);
  store.addActivity((wo.customer || wo.title || "Job") + " scheduled for "
    + parseYmd(dayStr).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    + (at != null ? " at " + fmtClock(at) : ""));
}

/* ---- capacity strip: are the next four weeks full or hungry? --------------
   Booked = crew-hours on scheduled jobs in the week; capacity = every crew's
   headcount × hours/day × 5. Answers "can we take this job next week?" at a
   glance, before drag-scheduling anything. ---- */
function CapacityStrip({ orders, crews }: any) {
  const capacity = (crews || []).reduce((a, c) => a + (window.VFP.crewHeadcount ? window.VFP.crewHeadcount(c) : (Number(c.size) || 2)) * (Number(c.hoursPerDay) || 8) * 5, 0);
  if (!capacity) return null;
  const mon = new Date(); mon.setHours(0, 0, 0, 0); mon.setDate(mon.getDate() - ((mon.getDay() + 6) % 7));
  const weeks = [0, 1, 2, 3].map(i => {
    const start = new Date(mon); start.setDate(start.getDate() + i * 7);
    const end = new Date(start); end.setDate(end.getDate() + 7);
    const booked = (orders || []).reduce((a, o) => {
      if (!o.scheduledStart || o.status === "completed") return a;
      const t = new Date(o.scheduledStart + "T00:00:00").getTime();
      return t >= start.getTime() && t < end.getTime() ? a + (Number(o.crewHours) || 0) : a;
    }, 0);
    return { start, booked, pct: Math.round((booked / capacity) * 100) };
  });
  const col = (p) => p >= 90 ? "var(--red)" : p >= 65 ? "var(--amber)" : "var(--teal)";
  const lb = (d, i) => i === 0 ? "This week" : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 14 }}>
      {weeks.map((w, i) => (
        <div key={i} className="card" style={{ padding: "10px 13px" }} title={`${Math.round(w.booked)} crew-hours booked of ~${Math.round(capacity)} available — ${w.pct >= 90 ? "full; quote further out" : w.pct >= 65 ? "filling up" : "room to sell"}`}>
          <div className="row between" style={{ fontSize: 11.5, marginBottom: 5 }}>
            <span className="muted" style={{ fontWeight: 700 }}>{lb(w.start, i)}</span>
            <span className="tnum" style={{ fontWeight: 800, color: col(w.pct) }}>{w.pct}%</span>
          </div>
          <div style={{ height: 6, borderRadius: 99, background: "var(--surface-3)", overflow: "hidden" }}>
            <div style={{ width: Math.min(100, w.pct) + "%", height: "100%", background: col(w.pct) }} />
          </div>
          <div className="faint" style={{ fontSize: 10, marginTop: 4 }}>{Math.round(w.booked)} of {Math.round(capacity)} crew-hrs</div>
        </div>
      ))}
    </div>
  );
}

function CalendarView({ store, go, params }: any) {
  const crews = (store.settings && store.settings.crews) || [];
  const orders = store.workOrders || [];
  const [cursor, setCursor] = React.useState<any>(() => new Date());   // board = this week; month view reads the month off it
  // opened for a specific day (a work order's "show on schedule", the sandbox tour): land on that week
  React.useEffect(() => {
    const d = params && params.day;
    if (!d || !/^\d{4}-\d{2}-\d{2}$/.test(String(d))) return;
    const t = new Date(String(d) + "T12:00:00");
    if (!isNaN(t.getTime())) setCursor(t);
  }, [params && params.day]);
  const [crewFilter, setCrewFilter] = React.useState<any>("all");
  // board = the interactive dispatch board (default — the #1 feature); month = classic agenda
  const [view, setView] = React.useState<any>("board");
  const [scope, setScope] = React.useState<any>("week");   // board scope: week | day | dayhours
  const [drag, setDrag] = React.useState<any>(null);        // { id, from: "tray"|"board" }
  const [hoverCell, setHoverCell] = React.useState<any>(null); // "crewId|ymd" or "crewId|ymd|HH:MM" hovered
  const [trayHover, setTrayHover] = React.useState<any>(false);
  const [daySheet, setDaySheet] = React.useState<any>(null); // crew day-view / assign modal
  const [trayOpen, setTrayOpen] = React.useState<any>(false); // mobile: show/hide the tray
  const [evModal, setEvModal] = React.useState<any>(null);    // "new" | an existing manual event → editor open
  const [gridCrew, setGridCrew] = React.useState<any>(null);   // mobile: which single crew the time-grid shows (null = unpicked → first lane)
  const narrow = useNarrow(640);
  // phones never show a side-by-side grid: a multi-crew Week collapses to one Day,
  // but the (vertical) hourly time-grid stays — it just shows one crew column.
  const effScope = narrow ? (scope === "dayhours" ? "dayhours" : "day") : scope;
  const isHours = effScope === "dayhours";   // hourly time-grid (Day · hours)

  const crewById = (id) => crews.find(c => c.id === id);
  // fall back to "all" if the filtered crew was deleted, so views never blank out
  const effFilter = (crewFilter === "all" || crewById(crewFilter)) ? crewFilter : "all";
  const monthName = cursor.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const todayStr = ymd(new Date());
  // the weather on every open job inside the forecast window (lib/schedulewx.ts)
  const wxHorizon = wxAddYmd(todayStr, 15);
  const wxJobs = React.useMemo(() => orders.filter((o: any) => o && o.scheduledStart && o.status !== "completed" && o.scheduledStart <= wxHorizon && (o.scheduledEnd || o.scheduledStart) >= todayStr), [orders, todayStr]);
  const wx = useBoardWeather(store, wxJobs);

  // ---------- month grid (classic agenda view) ----------
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const gridStart = addDays(first, -first.getDay());
  const weeks = [];
  for (let w = 0; w < 6; w++) { const days = []; for (let d = 0; d < 7; d++) days.push(addDays(gridStart, w * 7 + d)); weeks.push(days); }
  if (weeks[5].every(d => d.getMonth() !== cursor.getMonth())) weeks.pop();

  const jobsOn = (date) => {
    const ds = ymd(date);
    return orders.filter(o => {
      if (!o.scheduledStart) return false;
      if (effFilter !== "all" && o.crewId !== effFilter) return false;
      const s = o.scheduledStart, e = o.scheduledEnd || o.scheduledStart;
      return ds >= s && ds <= e;
    });
  };
  const unscheduled = orders.filter(o => o.status === "unscheduled");
  const scheduledCount = orders.filter(o => o.status === "scheduled").length;

  // manual events on a given day (sorted timed-first)
  const eventsOn = (dayStr) => calEventsOf(store)
    .filter(ev => ev.date === dayStr)
    .sort((a, b) => (hhmmToMin(a.time) ?? -1) - (hhmmToMin(b.time) ?? -1));

  async function doAuto() {
    // jobs with a change order awaiting approval need one confirmation before they go on the board
    const held = orders.filter(o => o.status === "unscheduled" && coHold(o, store).active);
    if (held.length) {
      const ok = await confirmDialog({ title: `${held.length} job${held.length > 1 ? "s have" : " has"} a change order awaiting approval`, body: held.map(o => o.customer || o.title).join(", ") + " — the price and scope aren't settled until the customer signs. Schedule anyway? This is recorded on each job.", confirmLabel: "Schedule anyway", danger: true });
      if (!ok) return;
      held.forEach(o => { const h = coHold(o, store); store.updateWorkOrder(o.id, { coHoldOverride: { at: Date.now(), ids: h.ids, action: "Schedule" } }); });
    }
    autoSchedule(store);
  }

  // ---------- board: day columns + crew lanes ----------
  const weekStart = addDays(cursor, -cursor.getDay());   // Sunday-anchored, matches the month grid
  const boardDays = effScope === "week"
    ? Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))
    : [new Date(cursor)];   // day + dayhours are single-day
  const dayStr0 = ymd(boardDays[0]);
  const lanes = crews.map(c => ({ id: c.id, name: c.name, color: c.color || "var(--teal)", crew: c }));
  // an "Unassigned" catch-all lane so scheduled-but-crewless jobs never hide
  const hasUnassigned = orders.some(o => (o.status === "scheduled" || o.status === "in_progress") && (!o.crewId || !crewById(o.crewId)));
  const visibleLanes = (effFilter === "all" ? lanes : lanes.filter(l => l.id === effFilter))
    .concat((effFilter === "all" && (hasUnassigned || !crews.length)) ? [{ id: "", name: "Unassigned", color: "var(--fg-faint)", crew: null }] : []);
  // the hourly time-grid reuses the same lanes as columns; on a phone it shows a
  // single crew (picked below) so the (vertical) timeline never overflows sideways.
  // Default to the first lane until the picker explicitly chooses one (gridCrew
  // is null while unpicked, so we don't land on an "Unassigned" id-"" lane).
  const pickedLane = gridCrew != null ? visibleLanes.find(l => l.id === gridCrew) : null;
  const gridLanes = (narrow && isHours && visibleLanes.length > 1)
    ? [pickedLane || visibleLanes[0]]
    : visibleLanes;

  // jobs that START on `date` in lane `crewId` (each block shows once, on its start day)
  const laneJobsStartingOn = (crewId, date) => {
    const ds = ymd(date);
    return orders.filter(o => {
      if (o.status !== "scheduled" && o.status !== "in_progress") return false;
      const oc = (o.crewId && crewById(o.crewId)) ? o.crewId : "";
      if (oc !== crewId) return false;
      return o.scheduledStart === ds;
    }).sort((a, b) => (b.priority ? 1 : 0) - (a.priority ? 1 : 0));
  };
  // multi-day jobs that merely SPAN `date` (continuation hint, not the start day)
  const laneJobsSpanning = (crewId, date) => {
    const ds = ymd(date);
    return orders.filter(o => {
      if (o.status !== "scheduled" && o.status !== "in_progress") return false;
      const oc = (o.crewId && crewById(o.crewId)) ? o.crewId : "";
      if (oc !== crewId) return false;
      const s = o.scheduledStart, e = o.scheduledEnd || o.scheduledStart;
      return s && ds > s && ds <= e;
    });
  };

  // ---------- drag/drop (native HTML5 DnD; no deps) ----------
  function onDragStart(e, id, from) {
    setDrag({ id, from });
    try { e.dataTransfer.setData("text/plain", id); e.dataTransfer.effectAllowed = "move"; } catch (err) { /* jsdom */ }
  }
  function onDragEnd() { setDrag(null); setHoverCell(null); setTrayHover(false); }
  async function onCellDrop(e, crewId, dayStr) {
    e.preventDefault();
    const id = (drag && drag.id) || (e.dataTransfer && e.dataTransfer.getData("text/plain"));
    setHoverCell(null);
    const wo = orders.find(o => o.id === id);
    if (!wo) { onDragEnd(); return; }
    if (wo.status === "unscheduled") { if (!(await confirmCoHold(wo, store, "Schedule"))) { onDragEnd(); return; } scheduleOnto(store, wo, dayStr, crewId); }
    else moveJob(store, wo, dayStr, crewId);
    onDragEnd();
  }
  // time-grid: dropped onto a half-hour slot in a crew column → set the time (+ crew)
  async function onSlotDrop(e, crewId, dayStr, hhmm) {
    e.preventDefault();
    const id = (drag && drag.id) || (e.dataTransfer && e.dataTransfer.getData("text/plain"));
    setHoverCell(null);
    const wo = orders.find(o => o.id === id);
    if (!wo) { onDragEnd(); return; }
    if (wo.status === "unscheduled") { if (!(await confirmCoHold(wo, store, "Schedule"))) { onDragEnd(); return; } scheduleOntoSlot(store, wo, dayStr, crewId, hhmm); }
    else slotJob(store, wo, dayStr, crewId, hhmm);
    onDragEnd();
  }
  // time-grid: dropped onto the all-day strip → keep it scheduled that day but clear
  // its time (back to "unslotted"); from the tray, schedule it day-level (no time).
  async function onUnslotDrop(e, crewId, dayStr) {
    e.preventDefault();
    const id = (drag && drag.id) || (e.dataTransfer && e.dataTransfer.getData("text/plain"));
    setHoverCell(null);
    const wo = orders.find(o => o.id === id);
    if (!wo) { onDragEnd(); return; }
    if (wo.status === "unscheduled") scheduleOnto(store, wo, dayStr, crewId);
    else {
      const patch: any = { scheduledTime: "" };
      if (crewId !== undefined) patch.crewId = crewId || "";
      store.updateWorkOrder(wo.id, patch);
    }
    onDragEnd();
  }
  // inline block editor commits a new time / duration
  function setSlotTime(wo, hhmm) { if (wo) store.updateWorkOrder(wo.id, { scheduledTime: hhmm || "" }); }
  function setSlotDuration(wo, min) { if (wo) store.updateWorkOrder(wo.id, { durationMin: Math.max(MIN_DURATION_MIN, Math.round(+min || 0)) }); }

  function onTrayDrop(e) {
    e.preventDefault();
    const id = (drag && drag.id) || (e.dataTransfer && e.dataTransfer.getData("text/plain"));
    setTrayHover(false);
    const wo = orders.find(o => o.id === id);
    // dropping a scheduled job back onto the tray → unschedule it (clear time too)
    if (wo && wo.status !== "unscheduled") {
      gcalAutoRemove(store, wo);   // 2-way sync: unscheduling removes the Google event
      store.updateWorkOrder(wo.id, { status: "unscheduled", scheduledStart: "", scheduledEnd: "", scheduledTime: "", calendarAdded: false, gcalEventId: null });
      store.addActivity((wo.customer || wo.title || "Job") + " returned to the unscheduled tray");
    }
    onDragEnd();
  }

  // ---------- nav ----------
  function step(dir) {
    setCursor(c => view === "month"
      ? new Date(c.getFullYear(), c.getMonth() + dir, 1)
      : addDays(c, dir * (effScope === "week" ? 7 : 1)));
  }
  function goToday() { const d = new Date(); setCursor(view === "month" ? new Date(d.getFullYear(), d.getMonth(), 1) : d); }
  const title = view === "month" ? monthName
    : effScope === "week" ? `${weekStart.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${addDays(weekStart, 6).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
    : cursor.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

  return (
    <div className="page fade-up" style={{ maxWidth: view === "board" && effScope === "week" ? 1480 : 1320 }}>
      <div className="row between" style={{ marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div><h1 className="display" data-tour="schedule-board" style={{ fontSize: 28, margin: 0 }}>Schedule</h1>
          <p className="muted" style={{ margin: "4px 0 0" }}>{scheduledCount} scheduled · {unscheduled.length} awaiting a date</p></div>
        <div className="row gap-8">
          {unscheduled.length > 0 && <button className="btn btn-primary" data-tour="auto-schedule" onClick={doAuto}><Icon name="bolt" size={15} />Auto-schedule {unscheduled.length}</button>}
          <button className="btn btn-ghost" onClick={() => go("workorders")}><Icon name="clipboard" size={15} />Work Orders</button>
        </div>
      </div>

      {/* how full are the next four weeks? */}
      <CapacityStrip orders={orders} crews={crews} />

      <div className={"cal-layout" + (view === "board" ? " board" : "")}>
        <div className="card" style={{ padding: 16, minWidth: 0 }}>
          {/* nav + view switcher */}
          <div className="row between" style={{ marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
            <div className="row gap-8">
              <button className="cal-nav" aria-label="Previous" onClick={() => step(-1)}><Icon name="chevright" size={16} style={{ transform: "rotate(180deg)" }} /></button>
              <button className="cal-nav" aria-label="Next" onClick={() => step(1)}><Icon name="chevright" size={16} /></button>
              <button className="btn btn-ghost btn-sm" onClick={goToday}>Today</button>
              <h2 className="display" style={{ fontSize: 18, margin: 0, marginLeft: 4 }}>{title}</h2>
            </div>
            <div className="row gap-8" style={{ flexWrap: "wrap" }}>
              {/* Board vs Month */}
              <div className="cat-tabs" style={{ margin: 0 }}>
                {[["board", "Board"], ["month", "Month"]].map(([id, lb]) => (
                  <button key={id} className={"filter-chip" + (view === id ? " on" : "")} onClick={() => setView(id)}>{lb}</button>
                ))}
              </div>
              {/* board scope: Day / Week, plus an "Hours" switch that turns the
                  day into the hourly time-grid. On phones Week collapses to Day. */}
              {view === "board" && (
                <div className="cat-tabs" style={{ margin: 0 }}>
                  {([["day", "Day"]] as any[])
                    .concat(narrow ? [] : [["week", "Week"]])
                    .map(([id, lb]) => {
                      const cur = narrow ? effScope : scope;
                      const on = id === "day" ? (cur === "day" || cur === "dayhours") : cur === id;
                      return <button key={id} className={"filter-chip" + (on ? " on" : "")} onClick={() => setScope(id)}>{lb}</button>;
                    })}
                  <button className={"filter-chip" + (isHours ? " on" : "")} title="Show the day as an hourly time-grid" aria-pressed={!!isHours} onClick={() => setScope(isHours ? "day" : "dayhours")}><Icon name="clock" size={13} />Hours</button>
                </div>
              )}
            </div>
          </div>

          {view === "board" && isHours ? (
            <>
              {/* phone: pick which crew's timeline to show */}
              {narrow && visibleLanes.length > 1 && (
                <div className="cal-grid-crewpick">
                  <label className="field-label" style={{ margin: 0 }}>Crew</label>
                  <select className="select" value={gridLanes[0] ? (gridLanes[0].id || "") : ""} onChange={e => setGridCrew(e.target.value)}>
                    {visibleLanes.map(l => <option key={l.id || "__none"} value={l.id || ""}>{l.name || "Crew"}</option>)}
                  </select>
                </div>
              )}
              <TimeGrid
                store={store} day={boardDays[0]} dayStr={dayStr0} lanes={gridLanes} todayStr={todayStr}
                drag={drag} hoverCell={hoverCell} setHoverCell={setHoverCell}
                onDragStart={onDragStart} onDragEnd={onDragEnd}
                onSlotDrop={onSlotDrop} onUnslotDrop={onUnslotDrop}
                setSlotTime={setSlotTime} setSlotDuration={setSlotDuration}
                crewById={crewById} go={go} wx={wx}
              />
            </>
          ) : view === "board" ? (
            <SchedulerBoard store={store} wx={wx}
              days={boardDays} lanes={visibleLanes} todayStr={todayStr}
              drag={drag} hoverCell={hoverCell} setHoverCell={setHoverCell}
              laneJobsStartingOn={laneJobsStartingOn} laneJobsSpanning={laneJobsSpanning}
              onDragStart={onDragStart} onDragEnd={onDragEnd} onCellDrop={onCellDrop}
              go={go} onOpenDay={(crewId, day) => setDaySheet({ crewId, day })}
              eventsOn={eventsOn} onOpenEvent={(ev) => setEvModal(ev)}
            />
          ) : (
            <>
              <div className="cal-grid cal-head">{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => <div key={d} className="cal-dow">{d}</div>)}</div>
              {weeks.map((week, wi) => (
                <div key={wi} className="cal-grid">
                  {week.map((date, di) => {
                    const inMonth = date.getMonth() === cursor.getMonth();
                    const isToday = ymd(date) === todayStr;
                    const jobs = jobsOn(date);
                    return (
                      <div key={di} className={"cal-cell" + (inMonth ? "" : " out") + (isToday ? " today" : "") + (isWeekend(date) ? " wknd" : "")} style={{ cursor: "pointer" }} onClick={() => { setCursor(new Date(date)); setView("board"); setScope("day"); }}>
                        <div className="cal-date">{date.getDate()}</div>
                        <div className="cal-jobs">
                          {eventsOn(ymd(date)).slice(0, 2).map(ev => (
                            <button key={ev.id} className="cal-job" style={{ background: "transparent", border: "1px dashed var(--line-2)", color: "var(--fg-2)" }}
                              title={(ev.time ? fmtClock(hhmmToMin(ev.time)) + " · " : "") + ev.title}
                              onClick={(e) => { e.stopPropagation(); setEvModal(ev); }}>
                              {ev.time ? fmtClock(hhmmToMin(ev.time)) + " " : ""}{ev.title || "Event"}
                            </button>
                          ))}
                          {jobs.slice(0, 3).map(j => { const cr = crewById(j.crewId); const isStart = j.scheduledStart === ymd(date); return (
                            <button key={j.id} data-cal-wo={j.id} className="cal-job" style={{ background: (cr && cr.color) || "var(--teal)" }} title={`${j.customer || j.title} · ${cr ? cr.name : "Unassigned"}`}
                              onClick={(e) => { e.stopPropagation(); go("workorders", { openWorkOrder: j.id }); }}>
                              {isStart ? (j.customer || j.title || "Job") : "···"}
                            </button>); })}
                          {jobs.length > 3 && <div className="cal-more">+{jobs.length - 3}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </>
          )}

          {/* legend / crew filter */}
          <div className="row gap-12" style={{ marginTop: 14, flexWrap: "wrap" }}>
            <button className={"cal-leg" + (crewFilter === "all" ? " on" : "")} onClick={() => setCrewFilter("all")}>All crews</button>
            {crews.map(c => <button key={c.id} className={"cal-leg" + (crewFilter === c.id ? " on" : "")} onClick={() => setCrewFilter(c.id)}><span style={{ width: 9, height: 9, borderRadius: 3, background: c.color }} />{c.name}</button>)}
          </div>
          {view === "board" && (
            <p className="faint" style={{ fontSize: 11.5, margin: "10px 0 0" }}>
              <Icon name="bolt" size={11} style={{ verticalAlign: "-1px" }} />{" "}
              {isHours
                ? <>Drag a job onto a time slot to set its appointment time, or to another crew column to reassign. Drag from the tray to schedule at that time; drag to the all-day strip to clear the time. Click a block to fine-tune its start &amp; length.</>
                : <>Drag a job between days to reschedule, or to another crew's lane to reassign. Drag from the tray to schedule. Switch to <b style={{ color: "var(--fg-2)" }}>Day · hours</b> for appointment times. On a phone, tap <Icon name="plus" size={10} style={{ verticalAlign: "-1px" }} /> on a tray job to place it.</>}
            </p>
          )}
        </div>

        {/* right rail: unscheduled tray (also a drop target) */}
        <aside className={"set-side cal-tray" + (trayOpen ? " open" : "")}>
          <div className={"card pad cal-tray-card" + (trayHover ? " drop" : "")}
            onDragOver={(e) => { if (drag) { e.preventDefault(); setTrayHover(true); } }}
            onDragLeave={() => setTrayHover(false)}
            onDrop={onTrayDrop}>
            <div className="row between" style={{ marginBottom: 10 }}><span className="kicker">Needs Scheduling</span><span className="chip amber" style={{ fontSize: 11 }}>{unscheduled.length}</span></div>
            {unscheduled.length === 0
              ? <p className="muted" style={{ fontSize: 12.5, textAlign: "center", margin: 0 }}>
                  Everything's on the calendar 🎉
                  {drag && drag.from === "board" && <><br /><span className="faint" style={{ fontSize: 11 }}>Drop here to unschedule</span></>}
                </p>
              : <div style={{ display: "grid", gap: 8 }}>
                  {unscheduled.map(o => (
                    <TrayCard store={store} key={o.id} wo={o} dragging={!!(drag && drag.id === o.id)}
                      crews={crews} go={go}
                      onDragStart={(e) => onDragStart(e, o.id, "tray")} onDragEnd={onDragEnd}
                      onPick={() => setDaySheet({ assignWo: o.id })} />
                  ))}
                  <button className="btn btn-primary btn-sm btn-block" style={{ marginTop: 4 }} onClick={doAuto}><Icon name="bolt" size={13} />Auto-schedule all</button>
                </div>}
          </div>
          <CalendarSyncCard store={store} go={go} onNewEvent={() => setEvModal("new")} />
          <div className="card pad cal-tray-aux">
            <div className="kicker" style={{ marginBottom: 8 }}>How scheduling works</div>
            <p className="muted" style={{ fontSize: 12, lineHeight: 1.55, margin: 0 }}>Drag jobs from this tray onto a crew's lane &amp; day to schedule them. Move blocks around the board to re-time a job or hand it to another crew. <b style={{ color: "var(--fg-2)" }}>Auto-schedule</b> packs everything onto the next open workdays in one click.</p>
          </div>
        </aside>
      </div>

      {/* mobile: floating tray toggle */}
      {unscheduled.length > 0 && (
        <button className="cal-tray-fab" onClick={() => setTrayOpen(o => !o)}>
          <Icon name={trayOpen ? "x" : "calendar"} size={16} />{trayOpen ? "Close" : `${unscheduled.length} to schedule`}
        </button>
      )}

      {/* manual event editor (create / edit / add-to-calendar) */}
      {evModal && (
        <ManualEventModal store={store} event={evModal === "new" ? null : evModal}
          defaultDate={dayStr0} onClose={() => setEvModal(null)} />
      )}

      {/* crew day-sheet modal (the tech's day) + tray-job assign picker */}
      {daySheet && (
        <CrewDaySheet store={store} crews={crews} go={go} sheet={daySheet}
          boardDays={boardDays} cursor={cursor} wx={wx}
          onClose={() => setDaySheet(null)} />
      )}
    </div>
  );
}

/* ---------- the dispatch board: crew lanes × day columns ---------- */
/* ---------- weather on the board (plan §13 item 13; lib/schedulewx.ts) ----------
   Every scheduled job inside the forecast window gets graded for ITS work:
   rain and frost for a fence day, a dry slab / 40°F / the dew point for a
   coating, frozen ground for a lift (lib/weatherrules.ts through
   window.VFP_WEATHER.risk). The job's pin is wo.geo (or the quote's drawn
   site); a job with only an address is geocoded once through the shared
   queue and keeps the pin. Nothing is fetched in the sandbox. The server's
   overnight verdict (wo.weatherFlag — scheduler.js tickForecast) shows when
   the live forecast isn't in yet, and "Move to <day>" takes the job to the
   next workday the forecast grades clear for its whole length with the crew
   free (moveJob, reason "weather"). */
function wxOptsFor(wo: any, store: any) {
  if (verticalOf(wo) !== "concrete") return undefined;
  const q = wo.quoteId ? ((store.allQuotes || store.quotes || []) as any[]).find((x: any) => x && x.id === wo.quoteId) : null;
  const cfg = (((store.settings || {}).ai || {}).automationCfg || {}).reschedule_weather;
  return { vertical: "concrete", jobTypes: jobTypesOf(q || wo), cfg: cfg && cfg.byVertical ? cfg.byVertical.concrete : undefined };
}
function useBoardWeather(store: any, jobs: any[]) {
  const [wx, setWx] = React.useState<any>({});   // jobId → { fc, grade, risk }
  const key = jobs.map((j: any) => j.id + ":" + j.scheduledStart + ":" + (j.scheduledEnd || "") + ":" + (geoOf(j) ? 1 : 0)).join("|");
  React.useEffect(() => {
    const W: any = (window as any).VFP_WEATHER;
    const demo: any = (window as any).VFP_DEMO;
    if (!W || (demo && demo.active) || !jobs.length) return;
    let dead = false;
    (async () => {
      for (const j of jobs.slice(0, 40)) {
        if (dead) return;
        try {
          const q = j.quoteId ? ((store.allQuotes || store.quotes || []) as any[]).find((x: any) => x && x.id === j.quoteId) : null;
          let geo = geoOf(j, q);
          if (!geo && j.address) {
            const g: any = await throttledGeocode(j.address);
            if (g && g.lat != null) { geo = { lat: g.lat, lng: g.lng }; store.updateWorkOrder(j.id, { geo }); }
          }
          if (!geo || dead) continue;
          const fc = await W.forecast(geo.lat, geo.lng);
          if (!fc || dead) continue;
          const opts = wxOptsFor(j, store);
          const grade = (d: any) => W.risk(d, opts);
          const entry = { fc, grade, risk: jobWeather(fc, j.scheduledStart, j.scheduledEnd, grade) };
          setWx((p: any) => ({ ...p, [j.id]: entry }));
        } catch (e) { /* no fix for this job: it stays ungraded */ }
      }
    })();
    return () => { dead = true; };
  }, [key]);
  return wx;
}
/* the job's weather as the board shows it: the live grade's worst day, else the server's overnight flag */
function wxOf(wo: any, w: any, todayStr: string) {
  if (w && w.risk) return w.risk.level === "ok" ? null : { level: w.risk.level, label: w.risk.label, why: w.risk.why, day: w.risk.day, live: true };
  const f = wo && wo.weatherFlag;
  if (weatherFlagLive(f, todayStr)) return { level: f.level === "med" ? "med" : "high", label: f.work ? "no " + f.work : "weather", why: f.why || "", day: f.day, live: false };
  return null;
}
/* the next workday the forecast grades clear for the job's whole length, with the crew free */
function nextGoodFor(store: any, wo: any, w: any) {
  if (!w || !w.fc || !wo || !wo.scheduledStart) return null;
  const crew = ((store.settings && store.settings.crews) || []).find((c: any) => c.id === wo.crewId);
  const workdays = crew && Array.isArray(crew.workdays) && crew.workdays.length ? crew.workdays : workdaysOf(store);
  return nextGoodDay(w.fc, wo.scheduledStart, w.grade, { span: spanDays(wo), workdays, busy: (d: string) => crewBusyOn(store.allWorkOrders || store.workOrders, wo.crewId, d, wo.id) });
}
function WxChip({ wo, wx, todayStr, compact }: any) {
  const r = wxOf(wo, wx, todayStr);
  if (!r) return null;
  const why = (r.why || "") + (r.live ? "" : " (overnight forecast)") + (r.day && r.day !== wo.scheduledStart ? " — " + wxDayLabel(r.day) : "");
  // compact: the icon alone (a tiny time-grid block, an all-day chip) — the grade and the why on hover
  return <span className={"cb-wx " + r.level + (compact ? " compact" : "")} title={compact ? r.label + (why ? " — " + why : "") : why} aria-label={compact ? "Weather: " + r.label : undefined}>⛈{compact ? "" : " " + r.label}</span>;
}
/* the row under a day-sheet job: the grade and the one-tap move */
function WxMoveRow({ wo, wx, store, todayStr }: any) {
  const r = wxOf(wo, wx, todayStr);
  if (!r) return null;
  const nd = nextGoodFor(store, wo, wx);
  return (
    <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "center", justifyContent: "flex-end" }}>
      <span className={"cb-wx " + r.level} title={r.why}>⛈ {r.label}{r.day && r.day !== wo.scheduledStart ? " · " + wxDayLabel(r.day) : ""}</span>
      {nd
        ? <button className="btn btn-outline btn-sm" title={(r.why || "") + " The next workday the forecast grades clear for the whole job, with this crew free."} onClick={() => { moveJob(store, wo, nd, undefined, { reason: "weather" }); notify(`Moved to ${wxDayLabel(nd)} — the next clear day the crew is free.`); }}><Icon name="calendar" size={12} />Move to {wxDayLabel(nd)}</button>
        : r.live ? <span className="faint" style={{ fontSize: 11 }}>No clear day in the forecast window — pick one by hand.</span> : null}
    </div>
  );
}

function SchedulerBoard({ days, lanes, todayStr, drag, hoverCell, setHoverCell, laneJobsStartingOn, laneJobsSpanning, onDragStart, onDragEnd, onCellDrop, go, onOpenDay, eventsOn, onOpenEvent, store, wx }: any) {
  const dow = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const gridVars = { "--board-days": String(days.length) } as any;   // drives the column count

  if (!lanes.length) {
    return <div className="cal-board-empty">
      <Icon name="user" size={22} className="muted" />
      <p className="muted" style={{ margin: "8px 0 0", fontSize: 13 }}>Add a crew in Settings to start dispatching jobs to lanes.</p>
    </div>;
  }

  return (
    <div className="cal-board-wrap">
      <div className="cal-board" style={gridVars}>
        {/* header row: corner + day columns */}
        <div className="cal-board-corner">Crew</div>
        {days.map(date => {
          const isToday = ymd(date) === todayStr;
          return (
            <div key={ymd(date)} className={"cal-board-dayhead" + (isToday ? " today" : "") + (isWeekend(date) ? " wknd" : "")}>
              <span className="cbd-dow">{dow[date.getDay()]}</span>
              <span className="cbd-num">{date.getDate()}</span>
              {isToday && <span className="cbd-today">Today</span>}
              {/* manual events (appointments, reminders) ride the day header */}
              {eventsOn && eventsOn(ymd(date)).slice(0, 2).map(ev => (
                <button key={ev.id} title={(ev.time ? fmtClock(hhmmToMin(ev.time)) + " · " : "") + (ev.title || "Event")}
                  onClick={(e) => { e.stopPropagation(); onOpenEvent && onOpenEvent(ev); }}
                  style={{ display: "block", width: "100%", marginTop: 3, padding: "1px 5px", fontSize: 10, fontWeight: 600, textAlign: "left",
                    border: "1px dashed var(--line-2)", borderRadius: 5, background: "transparent", color: "var(--fg-2)", cursor: "pointer",
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {ev.time ? fmtClock(hhmmToMin(ev.time)) + " " : ""}{ev.title || "Event"}
                </button>
              ))}
            </div>
          );
        })}

        {/* one row per crew lane */}
        {lanes.map(lane => (
          <React.Fragment key={lane.id || "__none"}>
            <div className="cal-board-lane-label" style={{ borderLeft: `3px solid ${lane.color}` }}>
              <span className="cbl-dot" style={{ background: lane.color }} />
              <span className="cbl-name">{lane.name || "Crew"}</span>
              {lane.crew && lane.crew.foreman && <span className="cbl-sub">{lane.crew.foreman}</span>}
            </div>
            {days.map(date => {
              const dayStr = ymd(date);
              const cellKey = (lane.id || "") + "|" + dayStr;
              const isHover = hoverCell === cellKey && !!drag;
              const isToday = dayStr === todayStr;
              const starts = laneJobsStartingOn(lane.id || "", date);
              const cont = laneJobsSpanning(lane.id || "", date);
              const total = starts.reduce((a, j) => a + (Number(j.total) || 0), 0);
              const filled = starts.length || cont.length;
              return (
                <div key={cellKey}
                  className={"cal-board-cell" + (isHover ? " drop" : "") + (isToday ? " today" : "") + (isWeekend(date) ? " wknd" : "") + (filled ? " filled" : " empty")}
                  onDragOver={(e) => { if (drag) { e.preventDefault(); if (hoverCell !== cellKey) setHoverCell(cellKey); } }}
                  onDragLeave={() => { if (hoverCell === cellKey) setHoverCell(null); }}
                  onDrop={(e) => onCellDrop(e, lane.id || "", dayStr)}
                  onClick={() => { if (filled) onOpenDay(lane.id || "", dayStr); }}
                >
                  {/* multi-day continuations from earlier in the week */}
                  {cont.map(j => (
                    <div key={"c-" + j.id} className="cal-block cont" style={{ background: laneTint(lane.color) }} title={`${j.customer || j.title} — continues`}>
                      <span className="cb-cont-bar" style={{ background: lane.color }} />
                      <span className="cb-cust-name">{j.customer || j.title || "Job"}</span>
                      <span className="cb-cont-tag">cont’d</span>
                    </div>
                  ))}
                  {/* jobs that start today */}
                  {starts.map(j => {
                    const span = spanDays(j);
                    return (
                      <div key={j.id} data-cal-wo={j.id}
                        className={"cal-block" + (drag && drag.id === j.id ? " dragging" : "") + (j.status === "in_progress" ? " active" : "")}
                        style={{ borderLeft: `3px solid ${lane.color}`, background: laneTint(lane.color) }}
                        draggable
                        onDragStart={(e) => { e.stopPropagation(); onDragStart(e, j.id, "board"); }}
                        onDragEnd={onDragEnd}
                        onClick={(e) => { e.stopPropagation(); go("workorders", { openWorkOrder: j.id }); }}
                        title={`${j.customer || j.title} · ${CAL_money0(j.total || 0)}${span > 1 ? " · " + span + " days" : ""} — drag to move, click to open`}
                      >
                        <div className="cb-cust">
                          {j.priority && <span className="cb-prio" title="Priority"><Icon name="bolt" size={9} /></span>}
                          {coHold(j, store).pending.length > 0 && <span className="cb-co" title="Change order awaiting the customer's approval">CO</span>}
                          <span className="cb-cust-name">{j.customer || j.title || "Job"}</span>
                          {j.status === "in_progress" && <span className="cb-live">live</span>}
                        </div>
                        {j.address && <div className="cb-addr">{j.address}</div>}
                        <div className="cb-meta">
                          <span className="cb-total">{CAL_money0(j.total || 0)}</span>
                          {span > 1 && <span className="cb-span">{span}d</span>}
                          <WxChip wo={j} wx={wx && wx[j.id]} todayStr={todayStr} />
                        </div>
                      </div>
                    );
                  })}
                  {/* empty-cell drop affordance */}
                  {!filled && <div className="cal-board-drop">{isHover ? "Drop to schedule" : ""}</div>}
                  {starts.length > 1 && <div className="cal-board-cellsum">{starts.length} jobs · {CAL_money0(total)}</div>}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

/* ---------- the hourly time-grid: time rows × crew columns ----------
   Day · hours. Each crew is a column; the day runs GRID_START_HOUR→GRID_END_HOUR
   down the page. A SLOTTED job (has scheduledTime) is an absolutely-positioned
   block at its start time, sized by its duration; an UNSLOTTED job (scheduled
   that day, no time) rides the all-day strip up top until it's dropped on a slot.
   Drops land on invisible half-hour cells, so native HTML5 DnD snaps to the slot
   without any pointer math. */
function TimeGrid({ store, day, dayStr, lanes, todayStr, drag, hoverCell, setHoverCell, onDragStart, onDragEnd, onSlotDrop, onUnslotDrop, setSlotTime, setSlotDuration, crewById, go, wx }: any) {
  const orders = store.workOrders || [];
  const [editId, setEditId] = React.useState<any>(null);   // block whose inline editor is open
  const isToday = dayStr === todayStr;
  const startMin = GRID_START_HOUR * 60, endMin = GRID_END_HOUR * 60;
  const slots = [];   // half-hour rows, e.g. 07:00, 07:30, …
  for (let m = startMin; m < endMin; m += SLOT_MIN) slots.push(m);
  const bodyH = (endMin - startMin) * PX_PER_MIN;
  const gridVars = { "--grid-cols": String(lanes.length), "--grid-body-h": bodyH + "px", "--grid-slot-h": (SLOT_MIN * PX_PER_MIN) + "px" } as any;

  // jobs scheduled on this day for a given crew column (crewId "" = Unassigned)
  const dayJobs = (crewId) => orders.filter(o => {
    if (o.status !== "scheduled" && o.status !== "in_progress") return false;
    const oc = (o.crewId && crewById(o.crewId)) ? o.crewId : "";
    if (oc !== crewId) return false;
    const s = o.scheduledStart, e = o.scheduledEnd || o.scheduledStart;
    return s && dayStr >= s && dayStr <= e;   // includes multi-day jobs that span today
  });
  const slottedOf = (crewId) => dayJobs(crewId).filter(o => hhmmToMin(o.scheduledTime) != null)
    .sort((a, b) => (hhmmToMin(a.scheduledTime)! - hhmmToMin(b.scheduledTime)!));
  const unslottedOf = (crewId) => dayJobs(crewId).filter(o => hhmmToMin(o.scheduledTime) == null);

  if (!lanes.length) {
    return <div className="cal-board-empty">
      <Icon name="user" size={22} className="muted" />
      <p className="muted" style={{ margin: "8px 0 0", fontSize: 13 }}>Add a crew in Settings to dispatch jobs to hourly time slots.</p>
    </div>;
  }

  return (
    <div className="cal-tg-wrap">
      {/* while dragging, let drop slots underneath receive the drop even where a
          block covers them (blocks go pointer-events:none via .dragging-active) */}
      <div className={"cal-tg" + (drag ? " dragging-active" : "")} style={gridVars}>
        {/* header: corner + crew column heads */}
        <div className="cal-tg-corner">Time</div>
        {lanes.map(lane => (
          <div key={(lane.id || "__none") + "-h"} className="cal-tg-colhead" style={{ borderTop: `3px solid ${lane.color}` }}>
            <span className="cbl-name">{lane.name || "Crew"}</span>
            {lane.crew && lane.crew.foreman && <span className="cbl-sub">{lane.crew.foreman}</span>}
          </div>
        ))}

        {/* all-day strip: scheduled-today-but-untimed jobs, one drop zone per crew */}
        <div className="cal-tg-allday-lbl">All-day</div>
        {lanes.map(lane => {
          const cid = lane.id || "";
          const key = "allday|" + cid;
          const hov = hoverCell === key && !!drag;
          const items = unslottedOf(cid);
          return (
            <div key={key} className={"cal-tg-allday" + (hov ? " drop" : "")}
              onDragOver={(e) => { if (drag) { e.preventDefault(); if (hoverCell !== key) setHoverCell(key); } }}
              onDragLeave={() => { if (hoverCell === key) setHoverCell(null); }}
              onDrop={(e) => onUnslotDrop(e, cid, dayStr)}>
              {items.map(j => (
                <div key={j.id} className={"cal-tg-chip" + (drag && drag.id === j.id ? " dragging" : "")}
                  style={{ borderLeft: `3px solid ${lane.color}`, background: laneTint(lane.color) }}
                  draggable onDragStart={(e) => { e.stopPropagation(); onDragStart(e, j.id, "board"); }} onDragEnd={onDragEnd}
                  onClick={() => go("workorders", { openWorkOrder: j.id })}
                  title={`${j.customer || j.title} — no time set, drag onto a slot`}>
                  {j.priority && <span className="cb-prio"><Icon name="bolt" size={9} /></span>}
                  <span className="cb-cust-name">{j.customer || j.title || "Job"}</span>
                  <WxChip wo={j} wx={wx && wx[j.id]} todayStr={todayStr} compact />
                </div>
              ))}
              {!items.length && <span className="cal-tg-allday-hint">{hov ? "Drop (no time)" : "—"}</span>}
            </div>
          );
        })}

        {/* time gutter: hour labels down the left */}
        <div className="cal-tg-gutter" style={{ height: bodyH }}>
          {slots.map(m => (
            <div key={m} className={"cal-tg-tick" + (m % 60 === 0 ? " hour" : "")} style={{ height: SLOT_MIN * PX_PER_MIN }}>
              {m % 60 === 0 && <span>{fmtClock(m)}</span>}
            </div>
          ))}
        </div>

        {/* one timed column per crew: drop slots underneath + positioned blocks on top */}
        {lanes.map(lane => {
          const cid = lane.id || "";
          const slotted = slottedOf(cid);
          return (
            <div key={cid + "-col"} className={"cal-tg-col" + (isToday ? " today" : "")} style={{ height: bodyH }}>
              {/* half-hour drop cells (invisible grid; snaps native DnD to a slot) */}
              {slots.map(m => {
                const hhmm = minToHHMM(m);
                const key = cid + "|" + dayStr + "|" + hhmm;
                const hov = hoverCell === key && !!drag;
                return (
                  <div key={key} className={"cal-tg-slot" + (m % 60 === 0 ? " hour" : "") + (hov ? " drop" : "")}
                    style={{ height: SLOT_MIN * PX_PER_MIN }}
                    onDragOver={(e) => { if (drag) { e.preventDefault(); if (hoverCell !== key) setHoverCell(key); } }}
                    onDragLeave={() => { if (hoverCell === key) setHoverCell(null); }}
                    onDrop={(e) => onSlotDrop(e, cid, dayStr, hhmm)}>
                    {hov && <span className="cal-tg-slot-at">{fmtClock(m)}</span>}
                  </div>
                );
              })}
              {/* now-line on today */}
              {isToday && <NowLine startMin={startMin} endMin={endMin} />}
              {/* positioned job blocks */}
              {slotted.map(j => (
                <TimeBlock store={store} key={j.id} wo={j} lane={lane} startMin={startMin} endMin={endMin}
                  dragging={!!(drag && drag.id === j.id)} editing={editId === j.id}
                  onDragStart={(e) => { e.stopPropagation(); onDragStart(e, j.id, "board"); }} onDragEnd={onDragEnd}
                  onEdit={() => setEditId(j.id)} onCloseEdit={() => setEditId(null)}
                  setSlotTime={setSlotTime} setSlotDuration={setSlotDuration} go={go}
                  wx={wx && wx[j.id]} todayStr={todayStr} />
              ))}
            </div>
          );
        })}
      </div>
      <p className="faint" style={{ fontSize: 11, margin: "8px 0 0", textAlign: "center" }}>
        {day.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })} · {fmtClock(startMin)}–{fmtClock(endMin)}
      </p>
    </div>
  );
}

/* a single positioned appointment block on the time-grid. Customer · start–end ·
   address, with an inline time + duration editor (click to open). */
function TimeBlock({ wo, lane, startMin, endMin, dragging, editing, onDragStart, onDragEnd, onEdit, onCloseEdit, setSlotTime, setSlotDuration, go, store, wx, todayStr }: any) {
  const s = hhmmToMin(wo.scheduledTime);
  const dur = jobDurationMin(wo);
  const e = (s ?? startMin) + dur;
  const wxr = wxOf(wo, wx, todayStr);   // the job's weather grade, as the board and the day sheet show it (§13 item 13)
  // clamp the visible block to the grid window so an early/late/overlong job still shows
  const top = Math.max(0, ((s ?? startMin) - startMin) * PX_PER_MIN);
  const rawH = dur * PX_PER_MIN;
  const maxH = (endMin - (s ?? startMin)) * PX_PER_MIN;   // can't run past the grid bottom
  const h = Math.max(SLOT_MIN * PX_PER_MIN * 0.8, Math.min(rawH, maxH));
  const tiny = h < 42 && !editing;
  return (
    <div className={"cal-tg-block" + (dragging ? " dragging" : "") + (wo.status === "in_progress" ? " active" : "") + (tiny ? " tiny" : "") + (editing ? " editing" : "")}
      style={{ top, height: editing ? "auto" : h, minHeight: h, borderLeft: `3px solid ${lane.color}`, background: laneTint(lane.color) }}
      draggable={!editing} onDragStart={onDragStart} onDragEnd={onDragEnd}
      onClick={() => { if (!editing) onEdit(); }}
      title={`${wo.customer || wo.title || "Job"} · ${fmtClock(s)}–${fmtClock(e)}${wo.address ? " · " + wo.address : ""}${wxr ? ` · ⛈ ${wxr.label}${wxr.why ? " (" + wxr.why + ")" : ""}` : ""} — click to edit time & length`}>
      <div className="cb-cust">
        {wo.priority && <span className="cb-prio" title="Priority"><Icon name="bolt" size={9} /></span>}
        {coHold(wo, store).pending.length > 0 && <span className="cb-co" title="Change order awaiting approval — scheduling asks for confirmation">CO pending</span>}
        <span className="cb-cust-name">{wo.customer || wo.title || "Job"}</span>
        <VerticalChip record={wo} style={{ fontSize: 9.5, padding: "0 6px" }} />
        <WxChip wo={wo} wx={wx} todayStr={todayStr} compact={tiny} />
        {wo.status === "in_progress" && <span className="cb-live">live</span>}
        <button className="cal-tg-edit" title="Edit time & length" onClick={(ev) => { ev.stopPropagation(); editing ? onCloseEdit() : onEdit(); }}><Icon name="sliders" size={11} /></button>
      </div>
      {!tiny && <div className="cal-tg-time">{fmtClock(s)}–{fmtClock(e)}</div>}
      {!tiny && wo.address && <div className="cb-addr">{wo.address}</div>}
      {editing && (
        <div className="cal-tg-editor" onClick={(ev) => ev.stopPropagation()} draggable={false} onDragStart={(ev) => ev.preventDefault()}>
          <div className="cal-tg-editrow">
            <label>Start</label>
            <input type="time" className="select cal-tg-input" value={wo.scheduledTime || minToHHMM(startMin)} step={900}
              onChange={(ev) => setSlotTime(wo, ev.target.value)} />
          </div>
          <div className="cal-tg-editrow">
            <label>Length</label>
            <select className="select cal-tg-input" value={String(dur)} onChange={(ev) => setSlotDuration(wo, +ev.target.value)}>
              {[30, 60, 90, 120, 180, 240, 300, 360, 420, 480].map(m => <option key={m} value={m}>{fmtDurShort(m)}</option>)}
            </select>
          </div>
          <div className="row gap-8" style={{ marginTop: 2 }}>
            <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={() => { onCloseEdit(); go("workorders", { openWorkOrder: wo.id }); }}>Open job</button>
            <button className="btn btn-primary btn-sm" style={{ flex: 1 }} onClick={onCloseEdit}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* "30m" / "1h" / "1h 30m" — compact duration for the length picker */
function fmtDurShort(min) {
  const m = Math.max(0, Math.round(+min || 0));
  const h = Math.floor(m / 60), r = m % 60;
  if (!h) return r + "m";
  if (!r) return h + "h";
  return h + "h " + r + "m";
}

/* the "now" indicator line on today's column */
function NowLine({ startMin, endMin }: any) {
  const [now, setNow] = React.useState<any>(() => { const d = new Date(); return d.getHours() * 60 + d.getMinutes(); });
  React.useEffect(() => {
    const t = setInterval(() => { const d = new Date(); setNow(d.getHours() * 60 + d.getMinutes()); }, 60000);
    return () => clearInterval(t);
  }, []);
  if (now < startMin || now > endMin) return null;
  return <div className="cal-tg-now" style={{ top: (now - startMin) * PX_PER_MIN }} aria-hidden="true"><span /></div>;
}

/* a faint tinted fill derived from the crew's solid color, for block backgrounds */
function laneTint(color) {
  if (typeof color === "string" && color[0] === "#") {
    const h = color.replace("#", "");
    const n = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
    const r = parseInt(n.slice(0, 2), 16), g = parseInt(n.slice(2, 4), 16), b = parseInt(n.slice(4, 6), 16);
    if ([r, g, b].every(v => !isNaN(v))) return `rgba(${r},${g},${b},0.13)`;
  }
  return "var(--surface-2)";
}

/* ---------- unscheduled tray card (draggable) ---------- */
function TrayCard({ wo, dragging, crews, go, onDragStart, onDragEnd, onPick, store }: any) {
  const cr = crews.find(c => c.id === wo.crewId);
  return (
    <div className={"cal-queue cal-tray-item" + (dragging ? " dragging" : "")}
      draggable onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <span className="cal-grip" aria-hidden="true"><Icon name="sliders" size={13} /></span>
      <div style={{ flex: 1, minWidth: 0 }} onClick={() => go("workorders", { openWorkOrder: wo.id })}>
        <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{wo.customer || wo.title || "Job"}</div>
        <div className="muted" style={{ fontSize: 11.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {wo.address ? wo.address + " · " : ""}{CAL_money0(wo.total || 0)}{cr ? " · " + cr.name : ""}
        </div>
      </div>
      {/* mobile: explicit "schedule" affordance since native DnD doesn't fire on touch */}
      <button className="cal-tray-add" title="Schedule…" onClick={(e) => { e.stopPropagation(); onPick(); }}><Icon name="plus" size={13} /></button>
    </div>
  );
}

/* ---------- crew day-sheet: the tech's ordered day ----------
   Doubles as the mobile "assign a tray job" picker (sheet.assignWo). */
function CrewDaySheet({ store, crews, go, sheet, boardDays, cursor, onClose, wx }: any) {
  const assignMode = !!sheet.assignWo;
  const todayStr = ymd(new Date());
  const crewById = (id) => crews.find((c: any) => c.id === id);
  const wo0 = assignMode ? (store.workOrders || []).find((o: any) => o.id === sheet.assignWo) : null;

  // assign-picker state (mobile fallback for tray drag)
  const dayOptions = (boardDays && boardDays.length ? boardDays : [new Date(cursor)]);
  const [pickCrew, setPickCrew] = React.useState<any>(wo0 && wo0.crewId ? wo0.crewId : (crews[0] && crews[0].id) || "");
  const [pickDay, setPickDay] = React.useState<any>(ymd(dayOptions[0]));
  function doAssign() { if (wo0) { scheduleOnto(store, wo0, pickDay, pickCrew); onClose(); } }

  // "On my way" per-job state (which jobs just flashed a Sent confirmation)
  const [enrouteSent, setEnrouteSent] = React.useState<any>({});
  function onMyWay(j: any) {
    // user-initiated SMS → gate on usage credits (window.VFP_CREDITS, separate PR;
    // feature-detected, allows when the runtime is absent).
    const cr: any = (window as any).VFP_CREDITS;
    if (cr && cr.guard && !cr.guard(store, "sms", { go })) return;
    const c: any = (store.customers || []).find((x: any) => x.id === j.customerId) || {};
    if (outboxConfigured()) {
      // FW-007: the stamp and the text as one queued op — sent once, dated
      // when tapped, and kept on this device until there's signal
      const h: any = store.fieldOp({ kind: "job.enroute", jobId: j.id });
      h.sent(8000).then((r: any) => { if (r && r.outcome === "waiting") notify("Saved — the on-my-way goes out when you're back online.", { kind: "info" }); });
    } else {
      // the customer's portal timeline shows "Crew on the way" from this stamp
      store.updateWorkOrder(j.id, { enrouteAt: Date.now() });
      emitAutomation("crew.enroute", { customer: { id: j.customerId, name: j.customer || c.name, phone: c.phone, email: c.email }, job: { id: j.projectId || j.id, address: j.address || c.address } });
    }
    setEnrouteSent((m: any) => ({ ...m, [j.id]: true }));
    setTimeout(() => setEnrouteSent((m: any) => { const n = { ...m }; delete n[j.id]; return n; }), 2400);
  }

  // crew-day view data
  const crew = !assignMode ? crewById(sheet.crewId) : null;
  const dayStr = sheet.day;
  const dayDate = parseYmd(dayStr);
  const jobs = !assignMode ? (store.workOrders || []).filter((o: any) => {
    if (o.status !== "scheduled" && o.status !== "in_progress") return false;
    const oc = (o.crewId && crewById(o.crewId)) ? o.crewId : "";
    if (oc !== (sheet.crewId || "")) return false;
    const s = o.scheduledStart, e = o.scheduledEnd || o.scheduledStart;
    return s && dayStr >= s && dayStr <= e;
  }).sort((a: any, b: any) => (b.priority ? 1 : 0) - (a.priority ? 1 : 0)) : [];
  const dayTotal = jobs.reduce((a: number, j: any) => a + (Number(j.total) || 0), 0);
  const color = (crew && crew.color) || "var(--fg-faint)";

  const body = (
    <div className="hub-preview-overlay" data-dialog onClick={onClose}>
      <div className="hub-quote-modal cal-daysheet" onClick={(e) => e.stopPropagation()}>
        <div className="hub-preview-bar">
          {assignMode ? (
            <span className="row gap-8" style={{ fontSize: 13, fontWeight: 700 }}><Icon name="calendar" size={14} className="muted" />Schedule {wo0 ? (wo0.customer || wo0.title || "job") : "job"}</span>
          ) : (
            <span className="row gap-8" style={{ fontSize: 13, fontWeight: 700 }}>
              <span style={{ width: 9, height: 9, borderRadius: 3, background: color }} />
              {crew ? crew.name : "Unassigned"} · {dayDate ? dayDate.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" }) : ""}
            </span>
          )}
          <button className="btn btn-ghost btn-sm" onClick={onClose}><Icon name="x" size={14} /></button>
        </div>

        <div className="hub-quote-body" style={{ display: "grid", gap: 12 }}>
          {assignMode ? (
            <>
              {wo0 && (
                <div className="cal-daysheet-job" style={{ borderLeft: `3px solid ${(crewById(pickCrew) && crewById(pickCrew).color) || "var(--teal)"}` }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 13.5 }}>{wo0.customer || wo0.title || "Job"}</div>
                    <div className="muted" style={{ fontSize: 12 }}>{wo0.address || "No address"} · {CAL_money0(wo0.total || 0)}</div>
                  </div>
                </div>
              )}
              <div>
                <label className="field-label">Assign crew</label>
                <select className="select" value={pickCrew} onChange={e => setPickCrew(e.target.value)}>
                  <option value="">Unassigned</option>
                  {crews.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="field-label">Day</label>
                {/* a real date picker, not a dropdown limited to the currently
                    viewed board days — on a phone the board is forced to a single
                    day, so the old select could only ever schedule onto today's
                    view. This lets you drop the job on any date. */}
                <input className="input" type="date" value={pickDay} onChange={e => setPickDay(e.target.value)} />
              </div>
              <button className="btn btn-primary btn-block" onClick={doAssign}><Icon name="check" size={15} />Schedule it</button>
              <p className="faint" style={{ fontSize: 11.5, textAlign: "center", margin: 0 }}>Sets the start date &amp; sizes the job by the crew's daily capacity. Fine-tune the end date in the work order.</p>
            </>
          ) : (
            <>
              <div className="row between" style={{ fontSize: 12.5 }}>
                <span className="muted">{jobs.length} job{jobs.length === 1 ? "" : "s"} on site{crew && crew.foreman ? " · " + crew.foreman : ""}</span>
                <span className="tnum" style={{ fontWeight: 700, color: "var(--teal)" }}>{CAL_money0(dayTotal)}</span>
              </div>
              {jobs.length === 0
                ? <p className="muted" style={{ textAlign: "center", fontSize: 13, margin: "8px 0" }}>No jobs for this crew on this day.</p>
                : <div style={{ display: "grid", gap: 9 }}>
                    {jobs.map((j: any, i: number) => {
                      const multi = j.scheduledEnd && j.scheduledEnd !== j.scheduledStart;
                      return (
                        <div key={j.id} style={{ display: "grid", gap: 4 }}>
                          <button className="cal-daysheet-job" style={{ borderLeft: `3px solid ${color}` }} onClick={() => { go("workorders", { openWorkOrder: j.id }); onClose(); }}>
                            <span className="cds-num">{i + 1}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div className="row gap-8" style={{ minWidth: 0 }}>
                                {j.priority && <span className="cb-prio"><Icon name="bolt" size={9} /></span>}
                                <span style={{ fontWeight: 700, fontSize: 13.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{j.customer || j.title || "Job"}</span>
                                {j.status === "in_progress" && <span className="cb-live">live</span>}
                              </div>
                              <div className="muted" style={{ fontSize: 12 }}>{j.address || "No address"}</div>
                              <div className="faint" style={{ fontSize: 11.5 }}>
                                {CAL_money0(j.total || 0)}
                                {j.crewHours ? " · " + j.crewHours + " crew-hrs" : ""}
                                {multi ? " · thru " + parseYmd(j.scheduledEnd).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}
                              </div>
                            </div>
                            <Icon name="chevright" size={14} className="muted" />
                          </button>
                          {/* the forecast's verdict on this job and the one-tap move to the next clear day (plan §13 item 13) */}
                          <WxMoveRow wo={j} wx={wx && wx[j.id]} store={store} todayStr={todayStr} />
                          {/* "On my way" → texts the homeowner the crew's enroute */}
                          <div className="row" style={{ justifyContent: "flex-end" }}>
                            {enrouteSent[j.id]
                              ? <span className="chip green" style={{ fontSize: 11 }}><Icon name="check" size={11} />On-my-way sent</span>
                              : <button className="btn btn-ghost btn-sm" onClick={() => onMyWay(j)} title="Text the customer that the crew is on the way"><Icon name="phone" size={13} />On my way</button>}
                          </div>
                        </div>
                      );
                    })}
                  </div>}
              <button className="btn btn-ghost btn-block btn-sm" onClick={onClose}>Close</button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal ? ReactDOM.createPortal(body, document.body) : body;
}

/* ---------- Calendar sync card (right rail) ----------
   Pick the calendar the shop runs on (Google / Apple / Outlook), see & control
   sync, create manual events, and open the synced calendar in its own app. */
function CalendarSyncCard({ store, go, onNewEvent }: any) {
  const { provider, twoWay } = calSyncOf(store);
  const connected = !!gcalConn(store);
  const cs = (store.settings && store.settings.calendarSync) || {};
  const setCS = (patch) => store.setSettings({ calendarSync: { ...cs, ...patch } });
  /* FW-027: `twoWay` lived only in this settings blob, which the scheduler
     never reads — so the switch that decides whether Google may move a job was
     invisible to the thing that moves it. It's mirrored to the integration row
     now, and the row is what the pull obeys. */
  const [gstate, setGstate] = React.useState<any>(null);
  React.useEffect(() => {
    const DB: any = (window as any).VFP_DB;
    if (!connected || !DB || !DB.gcalState || !DB.apiBase || !DB.apiBase()) return;
    let alive = true;
    DB.gcalState().then((d: any) => { if (alive && d) setGstate(d); }).catch(() => {});
    return () => { alive = false; };
  }, [connected]);
  const setTwoWay = (on: boolean) => {
    setCS({ twoWay: on });
    const DB: any = (window as any).VFP_DB;
    if (DB && DB.gcalState && DB.apiBase && DB.apiBase()) {
      DB.gcalState({ twoWay: on }).then((d: any) => setGstate(d)).catch(() => {
        notify("Saved here, but the service didn't get it — Google may keep pushing one way until it does.", { kind: "error" });
      });
    }
  };
  const csSyncedAt = gstate && gstate.syncedAt;
  const PROVIDERS = [["google", "Google"], ["apple", "Apple"], ["outlook", "Outlook"]];
  const evCount = calEventsOf(store).length;

  return (
    <div className="card pad cal-tray-aux">
      <div className="row between" style={{ marginBottom: 8 }}>
        <span className="kicker kicker-teal">Calendar sync</span>
        {provider === "google" && connected && <span className="chip green" style={{ fontSize: 10.5 }}><Icon name="check" size={11} />Synced</span>}
      </div>

      {/* which calendar do you live in? */}
      <div className="cat-tabs" style={{ margin: "0 0 10px" }}>
        {PROVIDERS.map(([id, lb]) => (
          <button key={id} className={"filter-chip" + (provider === id ? " on" : "")}
            onClick={() => setCS({ provider: provider === id ? "" : id })}>{lb}</button>
        ))}
      </div>

      {provider === "google" && (connected ? (
        <>
          <label className="row gap-8" style={{ fontSize: 12.5, cursor: "pointer", marginBottom: 8, alignItems: "flex-start" }}>
            <input type="checkbox" checked={gstate ? gstate.twoWay !== false : twoWay} onChange={(e: any) => setTwoWay(e.target.checked)} style={{ marginTop: 2 }} />
            {/* FW-027: this used to end "An event deleted on Google is restored
                on the next change here" — an accurate description of a push-only
                integration sitting under a heading that said 2-way. The pull
                exists now, so both directions are described. */}
            <span><b style={{ color: "var(--fg-2)" }}>2-way sync</b> — scheduling here creates the Google event; reschedules update it and unscheduling removes it. Moving or deleting the event <i>in Google Calendar</i> moves or unschedules the job here, within about ten minutes. If you change the same job on both sides, the later change wins.</span>
          </label>
          <p className="faint" style={{ fontSize: 11, margin: "0 0 8px" }}>
            {csSyncedAt
              ? `Last checked Google ${new Date(csSyncedAt).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}.`
              : "Fence Works checks Google every 10 minutes for changes you made there."}
            {" "}Turn this off to keep pushing to Google without letting it move anything here.
          </p>
          <a className="btn btn-outline btn-sm btn-block" href="https://calendar.google.com/calendar/r" target="_blank" rel="noopener"><Icon name="calendar" size={13} />Open Google Calendar</a>
        </>
      ) : (
        <>
          <p className="muted" style={{ fontSize: 12, lineHeight: 1.5, margin: "0 0 8px" }}>Connect Google Calendar and every job you schedule here lands on it automatically — customer, address, crew &amp; details.</p>
          <button className="btn btn-primary btn-sm btn-block" onClick={() => go("integrations")}><Icon name="bolt" size={13} />Connect Google Calendar</button>
        </>
      ))}
      {provider === "apple" && (
        <>
          <p className="muted" style={{ fontSize: 12, lineHeight: 1.5, margin: "0 0 8px" }}>Apple Calendar imports .ics files — export your schedule and open it in the Calendar app on Mac or iPhone. Full 2-way sync is live for Google today.</p>
          <button className="btn btn-outline btn-sm btn-block" onClick={() => exportScheduleIcs(store)}><Icon name="calendar" size={13} />Export to Calendar app (.ics)</button>
        </>
      )}
      {provider === "outlook" && (
        <>
          <p className="muted" style={{ fontSize: 12, lineHeight: 1.5, margin: "0 0 8px" }}>Import the .ics export into Outlook, or add events one at a time from any event or work order. Full 2-way sync is live for Google today.</p>
          <div style={{ display: "grid", gap: 6 }}>
            <a className="btn btn-outline btn-sm btn-block" href="https://outlook.live.com/calendar/" target="_blank" rel="noopener"><Icon name="calendar" size={13} />Open Outlook Calendar</a>
            <button className="btn btn-ghost btn-sm btn-block" onClick={() => exportScheduleIcs(store)}><Icon name="send" size={13} />Download schedule (.ics)</button>
          </div>
        </>
      )}
      {!provider && (
        <p className="muted" style={{ fontSize: 12, lineHeight: 1.5, margin: "0 0 8px" }}>Pick the calendar you run the business on. Google syncs 2-way; Apple &amp; Outlook get one-tap exports.</p>
      )}

      <button className="btn btn-ghost btn-sm btn-block" style={{ marginTop: 8 }} onClick={onNewEvent}>
        <Icon name="plus" size={13} />New event{evCount ? <span className="faint" style={{ fontSize: 10.5 }}> · {evCount} on the calendar</span> : null}
      </button>
    </div>
  );
}

/* ---------- manual event editor ----------
   Creates/edits a calendar event that isn't a work order (supplier meeting,
   inspection, day off). Push it to whichever calendar with the add buttons. */
function ManualEventModal({ store, event, defaultDate, onClose }: any) {
  const { provider } = calSyncOf(store);
  const [f, setF] = React.useState<any>(() => event
    ? { ...event, time: event.time || "", durationMin: event.durationMin || "", location: event.location || "", notes: event.notes || "" }
    : { title: "", date: defaultDate || ymd(new Date()), time: "", durationMin: "", location: "", notes: "" });
  const set = (patch) => setF((x) => ({ ...x, ...patch }));
  const valid = (f.title || "").trim() && f.date;
  const asEvent = () => ({
    id: event ? event.id : "ev-" + Date.now() + "-" + Math.floor(Math.random() * 1000),
    title: f.title.trim(), date: f.date, time: f.time || null,
    durationMin: Number(f.durationMin) > 0 ? Math.round(Number(f.durationMin)) : null,
    location: (f.location || "").trim() || null, notes: (f.notes || "").trim() || null,
    createdAt: event ? event.createdAt : Date.now(),
  });
  const save = () => { if (!valid) return; saveCalEvent(store, asEvent()); onClose(); };
  const del = async () => { if (event && await confirmDialog({ title: "Delete this event?", confirmLabel: "Delete event", danger: true })) { deleteCalEvent(store, event.id); onClose(); } };
  const addBtns = [
    ["google", "Google", () => window.open(evGoogleUrl(asEvent()), "_blank", "noopener")],
    ["outlook", "Outlook", () => window.open(evOutlookUrl(asEvent()), "_blank", "noopener")],
    ["apple", "Apple (.ics)", () => downloadIcsFile((f.title || "event").replace(/\s+/g, "-").toLowerCase() + ".ics", evVevent(asEvent()))],
  ].sort((a: any, b: any) => (b[0] === provider ? 1 : 0) - (a[0] === provider ? 1 : 0));   // the synced calendar leads

  return ReactDOM.createPortal(
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" style={{ width: "min(480px, 96vw)" }} onClick={(e: any) => e.stopPropagation()}>
        <h3 className="display" style={{ fontSize: 18, margin: "0 0 4px" }}>{event ? "Edit event" : "New event"}</h3>
        <p className="muted" style={{ fontSize: 12.5, margin: "0 0 12px" }}>Anything that belongs on the calendar but isn't a job — supplier meetings, inspections, time off.</p>
        <div style={{ display: "grid", gap: 10 }}>
          <div><label className="field-label">Title</label>
            <input className="input" value={f.title} placeholder="e.g. Inspector at the Miller job" onChange={(e: any) => set({ title: e.target.value })} /></div>
          <div className="row gap-8" style={{ flexWrap: "wrap" }}>
            <div><label className="field-label">Date</label>
              <input className="input" type="date" value={f.date} onChange={(e: any) => set({ date: e.target.value })} /></div>
            <div><label className="field-label">Time <span className="faint">(optional)</span></label>
              <input className="input" type="time" value={f.time} onChange={(e: any) => set({ time: e.target.value })} /></div>
            <div><label className="field-label">Minutes</label>
              <input className="input tnum" type="number" min="15" step="15" style={{ width: 84 }} value={f.durationMin} placeholder="60" onChange={(e: any) => set({ durationMin: e.target.value })} /></div>
          </div>
          <div><label className="field-label">Location <span className="faint">(optional)</span></label>
            <input className="input" value={f.location} onChange={(e: any) => set({ location: e.target.value })} /></div>
          <div><label className="field-label">Notes</label>
            <textarea className="input" rows={2} value={f.notes} onChange={(e: any) => set({ notes: e.target.value })} /></div>
        </div>
        <div className="row gap-8" style={{ marginTop: 14, flexWrap: "wrap" }}>
          <button className="btn btn-primary btn-sm" disabled={!valid} onClick={save}><Icon name="check" size={13} />{event ? "Save" : "Add to schedule"}</button>
          {valid && addBtns.map(([id, lb, fn]: any) => (
            <button key={id} className="btn btn-outline btn-sm" title={"Add this event to " + lb} onClick={fn}><Icon name="calendar" size={12} />{lb}</button>
          ))}
          <span style={{ flex: 1 }} />
          {event && <button className="btn btn-ghost btn-sm" style={{ color: "var(--red)" }} onClick={del}><Icon name="trash" size={13} />Delete</button>}
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

Object.assign(window, { CalendarView, autoSchedule });

export { workdaysOf, nextWeekday, woDays, spanDays, autoSchedule, moveJob, scheduleOnto, slotJob, scheduleOntoSlot, estimateDurationMin, jobDurationMin, hhmmToMin, minToHHMM, fmtClock, CalendarView, SchedulerBoard, TimeGrid, TimeBlock, CrewDaySheet, TrayCard, CAL_money0, ymd, parseYmd, addDays, DEFAULT_WORKDAYS, isWeekend };
